import { collection, doc, getDoc, getDocs, query, where, writeBatch, runTransaction, serverTimestamp, deleteField } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { billingRatesOnly, canReadFinancialKind, partitionFinancialData, privateFinancialId } from './financialBoundary';

export async function getFinancialAccess() {
  if (!auth.currentUser) return null;
  const snap = await getDoc(doc(db, 'accessProfiles', auth.currentUser.uid));
  return snap.exists() ? snap.data() : null;
}

export async function readPrivateFinancials(kind, sourceId, access = null) {
  const current = access || await getFinancialAccess();
  if (!canReadFinancialKind(current, kind)) throw new Error('Financial permission required.');
  const snap = await getDoc(doc(db, 'privateFinancials', privateFinancialId(`${kind}/${sourceId}`)));
  return snap.exists() ? snap.data().data || {} : {};
}

// Canonical creation uses one atomic batch. Ordinary Planners provide only the
// immutable rate reference; they cannot retrieve or supply worker pay values.
export async function persistOperationalShift(payload) {
  const { operational, financial } = partitionFinancialData(payload);
  const facility = await getDoc(doc(db, 'facilityProfiles', operational.facilityId));
  if (!facility.exists() || facility.data().tenantId !== operational.tenantId) throw new Error('A matching facility profile is required.');
  operational.compensationRateVersion = facility.data().compensationRateVersion || null;
  operational.financialDataVersion = 1;
  const ref = doc(collection(db, 'shifts'));
  const batch = writeBatch(db);
  if (Object.keys(financial).length) {
    const access = await getFinancialAccess();
    if (!canReadFinancialKind(access, 'shifts')) throw new Error('Private compensation requires authorized finance access.');
    batch.set(doc(db, 'privateFinancials', privateFinancialId(`shifts/${ref.id}`)), {
      kind: 'shifts', sourceId: ref.id, tenantId: operational.tenantId,
      facilityId: operational.facilityId, data: financial, updatedAt: serverTimestamp(),
    });
  }
  batch.set(ref, operational);
  await batch.commit();
  return ref;
}

export async function saveFacilityFinancialProfile(facilityId, payload) {
  const { operational, financial: suppliedFinancial } = partitionFinancialData(payload);
  const publicRef = doc(db, 'facilityProfiles', facilityId);
  const previous = await getDoc(publicRef);
  const tenantId = operational.tenantId || previous.data()?.tenantId;
  // A legacy public profile must move its private fields in the same batch.
  // Merely merging the public projection leaves the old rates behind and is
  // rejected by the operational-only rules. Never discard unsupplied values.
  const legacyFinancial = partitionFinancialData(previous.data() || {}).financial;
  const financial = { ...legacyFinancial, ...suppliedFinancial };
  const batch = writeBatch(db);
  if (Object.keys(financial).length) {
    const access = await getFinancialAccess();
    if (!canReadFinancialKind(access, 'facilityProfiles')) throw new Error('Rate permission required.');
    const privateRef = doc(db, 'privateFinancials', privateFinancialId(`facilityProfiles/${facilityId}`));
    const existing = await getDoc(privateRef);
    const canonicalFinancial = existing.data()?.data || {};
    Object.assign(financial, legacyFinancial, canonicalFinancial, suppliedFinancial);
    // Merge each role and its fields in precedence order. A partial pay-rate
    // edit must retain other roles and that role's independently stored bill rate.
    if ([legacyFinancial, canonicalFinancial, suppliedFinancial].some(source => 'rates' in source)) {
      const rates = {};
      for (const source of [legacyFinancial, canonicalFinancial, suppliedFinancial]) {
        if (!('rates' in source)) continue;
        if (!source.rates || typeof source.rates !== 'object' || Array.isArray(source.rates)) {
          throw new Error('Facility rates must be a role map.');
        }
        for (const [role, rate] of Object.entries(source.rates)) {
          if (!rate || typeof rate !== 'object' || Array.isArray(rate)) {
            throw new Error('Each facility role must contain rate fields.');
          }
          rates[role] = { ...rates[role], ...rate };
        }
      }
      financial.rates = rates;
    }
    batch.set(privateRef, { kind: 'facilityProfiles', sourceId: facilityId, tenantId, facilityId,
      data: { ...(existing.data()?.data || {}), ...financial }, updatedAt: serverTimestamp() });
    if (financial.rates) {
      const version = doc(collection(db, 'privateFinancials'));
      batch.set(version, { kind: 'rateVersion', sourceId: version.id, facilityId, tenantId,
        data: { rates: financial.rates }, createdAt: serverTimestamp() });
      operational.compensationRateVersion = version.id;
      operational.billingRates = billingRatesOnly(financial.rates);
    }
  }
  const removeLegacy = Object.fromEntries(Object.keys(legacyFinancial).map(key => [key, deleteField()]));
  batch.set(publicRef, { ...operational, ...removeLegacy, tenantId, facilityId }, { merge: true });
  await batch.commit();
  return { ...operational, ...financial };
}

export async function updateShiftFinancials(shift, patch) {
  const access = await getFinancialAccess();
  if (!canReadFinancialKind(access, 'shifts')) throw new Error('Payroll permission required.');
  const ref = doc(db, 'privateFinancials', privateFinancialId(`shifts/${shift.id}`));
  const { financial, operational } = partitionFinancialData(patch);
  if (Object.keys(operational).length) throw new Error('Use the canonical shift service for operational updates.');
  await runTransaction(db, async transaction => {
    const previous = await transaction.get(ref);
    transaction.set(ref, { kind: 'shifts', sourceId: shift.id, tenantId: shift.tenantId, facilityId: shift.facilityId,
      data: { ...(previous.data()?.data || {}), ...financial }, updatedAt: serverTimestamp() });
  });
}

export async function hydrateFinancialRecords(kind, rows, suppliedAccess = null) {
  const access = suppliedAccess || await getFinancialAccess();
  if (!canReadFinancialKind(access, kind)) return rows;
  const versions = new Map();
  return Promise.all(rows.map(async row => {
    if (row.tenantId !== access.tenantId) return row;
    const data = await readPrivateFinancials(kind, row.id || row.uid, access);
    let rates = {};
    if (kind === 'shifts' && row.compensationRateVersion) {
      if (!versions.has(row.compensationRateVersion)) versions.set(row.compensationRateVersion,
        getDoc(doc(db, 'privateFinancials', row.compensationRateVersion)).then(snap => snap.data()?.data?.rates || {}));
      rates = await versions.get(row.compensationRateVersion);
    }
    const role = String(row.role || '').toUpperCase().replace(/[\s-]+/g, '_');
    return { ...row, ...(rates[role] ? { payRate: rates[role].payRate } : {}), ...data };
  }));
}

export async function readTenantFinancialRecords(kind, access) {
  if (!canReadFinancialKind(access, kind)) return [];
  const constraints = [where('tenantId', '==', access.tenantId), where('kind', '==', kind)];
  if (!['owner_admin', 'admin', 'finance_admin'].includes(access.role)) constraints.push(where('facilityId', '==', access.facilityId));
  const snap = await getDocs(query(collection(db, 'privateFinancials'), ...constraints));
  return snap.docs.map(item => ({ id: item.id, ...item.data() }));
}
